import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Spin } from "antd";
import {
  SearchOutlined,
  ExportOutlined,
  DownloadOutlined,
  CloseOutlined,
  UserOutlined,
  TeamOutlined,
  BankOutlined,
  ApartmentOutlined,
  MedicineBoxOutlined,
  FileProtectOutlined,
  FileTextOutlined,
  CalendarOutlined,
  SolutionOutlined,
  ScheduleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import lib from "../../assets/utils/lib";
import config, { searchTarget } from "../../assets/utils/config";
import { useAuth } from "../../assets/utils/useAuth";
import { usePermissions } from "../../assets/utils/usePermissions";
import { useUnifiedSearch } from "../../hooks/useUnifiedSearch";
import {
  useUnifiedSearchCatalog,
  SEARCH_TARGET_CHIP_LABELS,
} from "../../hooks/useUnifiedSearchCatalog";
import { PolicyHolderModal } from "../../features/compoinents/userData.js";
import { ListOfDependent } from "../../features/compoinents/profileUpdate.js";
import { EnroleeBiodataModal } from "../../features/compoinents/enroleeData.js";
import { FacilityData } from "../../features/compoinents/FacilityData.js";
import { toFacilityProviderRecord } from "../../features/facilities/facilityUtils";
import { GlobalSearchResultList } from "./GlobalSearchResultList";
import FacilitiesExportModal from "../Facilities/FacilitiesExportModal";
import FacilitiesExportsDrawer from "../Facilities/FacilitiesExportsDrawer";
import { canExportFacilities } from "../Facilities/facilityExportHelpers";
import {
  buildGlobalSearchSession,
  getFacilityKeyForSession,
  loadGlobalSearchSession,
  saveGlobalSearchSession,
} from "./globalSearchSession";
import "./GlobalSearchOverlay.scss";

/** Must sit above the search shell (`zIndex={1100}`) so detail modals are visible. */
const GLOBAL_SEARCH_DETAIL_MODAL_Z_INDEX = 1150;
/** Export UI must sit above the search shell and detail modals. */
const GLOBAL_SEARCH_EXPORT_Z_INDEX = 1200;

const TARGET_ICONS = {
  clients: UserOutlined,
  enrollees: TeamOutlined,
  facilities: BankOutlined,
  corporations: ApartmentOutlined,
  constableItems: MedicineBoxOutlined,
  pa: FileProtectOutlined,
  claims: FileTextOutlined,
  checkins: CalendarOutlined,
  onboarding: SolutionOutlined,
  appointments: ScheduleOutlined,
};

function getChipLabel(opt) {
  return SEARCH_TARGET_CHIP_LABELS[opt.value] || opt.label;
}

const GlobalSearchOverlay = ({ open, onClose }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const categoriesRef = useRef(null);
  const { user } = useAuth();
  const { hasAnyPermission, hasAnyPermissionWithLegacy } = usePermissions();
  const facilityIdForCatalog =
    user?.facility_id ?? user?.facilityId ?? user?.profile?.facility_id ?? null;
  const facilityKey = getFacilityKeyForSession(facilityIdForCatalog);

  const {
    selectOptions: targetOptions,
    loading: catalogLoading,
    error: catalogError,
  } = useUnifiedSearchCatalog({
    enabled: open,
    facilityId: facilityIdForCatalog,
    fallbackOptions: searchTarget,
  });

  const [target, setTarget] = useState("enrollees");
  const [query, setQuery] = useState("");
  const [searched, setSearched] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [showingCached, setShowingCached] = useState(false);

  const wasOpenRef = useRef(false);
  const snapshotRef = useRef({});
  const skipTargetRefreshRef = useRef(false);
  const prevTargetRef = useRef(target);
  /** Last query/target that produced the current `results` list */
  const lastSearchRef = useRef({ query: "", target: "" });

  const {
    execute,
    cancel,
    reset,
    loading,
    results,
    setResults,
    feedback,
    setFeedback,
    pagination,
    searchMeta,
    setSearchMeta,
    setPagination,
  } = useUnifiedSearch({ manageLoading: true });

  const [rowData, setRowData] = useState({});
  const [showClient, setShowClient] = useState(false);
  const [showEnrollee, setShowEnrollee] = useState(false);
  const [openEncounterCodes, setOpenEncounterCodes] = useState(false);
  const [showClientEnrollee, setShowClientEnrollee] = useState(false);
  const [showFacility, setShowFacility] = useState(false);
  const [facilityExportOpen, setFacilityExportOpen] = useState(false);
  const [facilityExportsDrawerOpen, setFacilityExportsDrawerOpen] = useState(false);

  const showFacilityExport = target === "facilities" && canExportFacilities(user, hasAnyPermission);
  const canViewFacilityExports = canExportFacilities(user, hasAnyPermission);

  const facilityExportRowEstimate = useMemo(() => {
    const fromMeta = searchMeta?.results_count;
    if (fromMeta != null && !Number.isNaN(Number(fromMeta))) return Number(fromMeta);
    const fromPage = pagination?.total;
    if (fromPage != null && !Number.isNaN(Number(fromPage))) return Number(fromPage);
    return null;
  }, [searchMeta, pagination]);

  // Detail modals open read-only for everyone who may NOT edit; each modal still
  // gates its own writes behind its permission checks. (Until 2026-09-30 the polarity
  // was inverted here, so Super User / Customer Care / admin got a read-only client
  // modal and could not change details such as the policy holder's phone.)
  const canEdit = useCallback(
    () =>
      hasAnyPermissionWithLegacy(
        ['clients.update', 'clients.manage_all', 'facilities.update', 'admin.*'],
        [1, 2],
      ),
    [hasAnyPermissionWithLegacy],
  );

  const activeCatalogEntry = useMemo(
    () => targetOptions.find((o) => o.value === target)?.catalog,
    [targetOptions, target],
  );

  const activeCategoryLabel =
    targetOptions.find((o) => o.value === target)?.label || "Results";

  const allowEmptyForTarget = lib.effectiveUnifiedSearchAllowsEmptyQuery(
    target,
    activeCatalogEntry,
  );

  const minQueryLen = lib.effectiveUnifiedSearchMinQueryLength(
    target,
    activeCatalogEntry,
  );

  useEffect(() => {
    if (!open || !targetOptions.length) return;
    const ok = targetOptions.some((o) => o.value === target && !o.disabled);
    if (ok) return;
    const first = targetOptions.find((o) => !o.disabled);
    if (first) setTarget(first.value);
  }, [open, targetOptions, target]);

  useEffect(() => {
    if (open) {
      const t = window.setTimeout(() => inputRef.current?.focus(), 80);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [results, target]);

  snapshotRef.current = {
    query,
    target,
    searched,
    results,
    pagination,
    searchMeta,
    facilityKey,
  };

  const applySession = useCallback(
    (session) => {
      if (!session?.searched) return false;
      setQuery(session.query ?? "");
      setTarget(session.target ?? "enrollees");
      setSearched(true);
      setResults(session.results ?? []);
      setPagination(session.pagination ?? null);
      setSearchMeta(session.searchMeta ?? null);
      setFeedback(null);
      setShowingCached(true);
      skipTargetRefreshRef.current = true;
      prevTargetRef.current = session.target ?? "enrollees";
      lastSearchRef.current = {
        query: String(session.query ?? "").trim(),
        target: session.target ?? "enrollees",
      };
      return true;
    },
    [setResults, setPagination, setSearchMeta, setFeedback],
  );

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      const session = loadGlobalSearchSession(facilityKey);
      applySession(session);
    }

    if (!open && wasOpenRef.current) {
      cancel();
      const snap = snapshotRef.current;
      if (snap.searched && Array.isArray(snap.results)) {
        saveGlobalSearchSession(buildGlobalSearchSession(snap));
      }
      setShowingCached(false);
      setShowClient(false);
      setShowEnrollee(false);
      setShowClientEnrollee(false);
      setShowFacility(false);
      setRowData({});
    }

    wasOpenRef.current = open;
  }, [open, facilityKey, cancel, applySession]);

  useEffect(() => {
    if (!open || !categoriesRef.current) return;
    const activeChip = categoriesRef.current.querySelector(
      '[role="tab"][aria-selected="true"]',
    );
    activeChip?.scrollIntoView({ inline: "nearest", block: "nearest", behavior: "smooth" });
  }, [target, open, targetOptions]);

  const runSearch = useCallback(async () => {
    const q = query.trim();
    const catalogEntry = targetOptions.find((o) => o.value === target)?.catalog;
    const allowEmpty = lib.effectiveUnifiedSearchAllowsEmptyQuery(target, catalogEntry);
    if (!allowEmpty && !q) {
      cancel();
      setSearched(false);
      return;
    }
    const resource = lib.unifiedSearchTargetToResource(target);
    const extra = {};
    if (lib.unifiedSearchRequiresFacilityId(resource) && facilityIdForCatalog) {
      extra.facility_id = Number(facilityIdForCatalog);
    }
    setShowingCached(false);
    setSearched(true);
    const out = await execute({
      target,
      search: q,
      page: 1,
      catalogEntry,
      extra,
    });
    if (out.ok) {
      lastSearchRef.current = { query: q, target };
      saveGlobalSearchSession(
        buildGlobalSearchSession({
          query: q,
          target,
          searched: true,
          results: out.data ?? [],
          pagination: out.pagination ?? null,
          searchMeta: out.body?.search != null ? out.body.search : null,
          facilityKey,
        }),
      );
    }
  }, [
    query,
    target,
    targetOptions,
    facilityIdForCatalog,
    facilityKey,
    execute,
    cancel,
  ]);

  const refreshAfterDetail = useCallback(async () => {
    await runSearch();
  }, [runSearch]);

  const resultsMatchCurrentSearch = useCallback(() => {
    if (showingCached) return false;
    const q = query.trim();
    return (
      q === lastSearchRef.current.query &&
      target === lastSearchRef.current.target
    );
  }, [query, target, showingCached]);

  const rowSetting = useCallback(
    async (record, t = null) => {
      const action = t ?? target;
      if (action === "pa" || action === "claims" || action === "checkins") {
        lib.unifiedSearchOpenTarget(navigate, action, record);
        onClose();
        return;
      }
      setRowData(toFacilityProviderRecord(record));
      switch (action) {
        case "corporations":
          return;
        case "clients":
          setShowClient(true);
          break;
        case "enrollees":
          setOpenEncounterCodes(false);
          setShowEnrollee(true);
          break;
        case "enrollee_encounter_codes":
          setOpenEncounterCodes(true);
          setShowEnrollee(true);
          break;
        case "client_enrollees":
          setShowClientEnrollee(true);
          break;
        case "facilities":
          setShowFacility(true);
          break;
        default:
          return;
      }
    },
    [navigate, onClose, target],
  );

  const handleCancelEnrollees = () => {
    setShowEnrollee(false);
    setOpenEncounterCodes(false);
  };
  const handleCancelFacilities = () => setShowFacility((v) => !v);
  const handleCancelClientEnrollees = () => setShowClientEnrollee((v) => !v);

  useEffect(() => {
    if (!open || !searched) {
      prevTargetRef.current = target;
      return;
    }
    if (skipTargetRefreshRef.current) {
      skipTargetRefreshRef.current = false;
      prevTargetRef.current = target;
      return;
    }
    if (prevTargetRef.current !== target) {
      prevTargetRef.current = target;
      runSearch();
    }
  }, [target, open, searched, runSearch]);

  const goFullSearch = useCallback(() => {
    const q = query.trim();
    const catalogEntry = targetOptions.find((o) => o.value === target)?.catalog;
    if (!q && !lib.effectiveUnifiedSearchAllowsEmptyQuery(target, catalogEntry)) return;
    try {
      localStorage.setItem(config.key.search, JSON.stringify({ search: q, target }));
    } catch {
      /* ignore */
    }
    navigate("/search");
    onClose();
  }, [query, target, targetOptions, navigate, onClose]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (results.length > 0) {
          setActiveIndex((i) => Math.min(i + 1, results.length - 1));
        }
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        if (results.length > 0) {
          setActiveIndex((i) => Math.max(i - 1, 0));
        }
        return;
      }

      if (e.key === "Enter" && !e.metaKey && !e.ctrlKey) {
        const fromInput =
          e.target === inputRef.current ||
          inputRef.current?.contains(e.target);
        if (fromInput) return;

        if (
          results.length > 0 &&
          searched &&
          !loading &&
          resultsMatchCurrentSearch()
        ) {
          const record = results[activeIndex];
          if (record) {
            e.preventDefault();
            rowSetting(record, target);
          }
          return;
        }

        e.preventDefault();
        runSearch();
        return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        goFullSearch();
      }
    },
    [
      results,
      activeIndex,
      searched,
      loading,
      target,
      rowSetting,
      goFullSearch,
      onClose,
      runSearch,
      resultsMatchCurrentSearch,
    ],
  );

  const resultCount =
    pagination?.total ?? searchMeta?.results_count ?? results.length;

  const idleHint = allowEmptyForTarget
    ? "Press Enter to browse this category, or type to filter"
    : minQueryLen > 0
      ? `Type at least ${minQueryLen} characters, then press Enter`
      : "Type a name, ID, or term and press Enter";

  const handleCategoriesWheel = useCallback((e) => {
    const el = categoriesRef.current;
    if (!el || el.scrollWidth <= el.clientWidth) return;
    if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
    e.preventDefault();
    el.scrollLeft += e.deltaY;
  }, []);

  const sectionTitle = searched
    ? searchMeta?.query
      ? `Results for “${searchMeta.query}”`
      : `${activeCategoryLabel}${resultCount != null ? ` · ${resultCount} records` : ""}`
    : null;

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        closable={false}
        width="100%"
        centered={false}
        zIndex={1100}
        destroyOnClose={false}
        maskClosable={false}
        className="global-search-modal global-search-modal--fullscreen"
        style={{ top: 0, padding: 0, margin: 0, maxWidth: "100vw" }}
        styles={{
          content: {
            margin: 0,
            padding: 0,
            top: 0,
            width: "100vw",
            maxWidth: "100vw",
            height: "100dvh",
            maxHeight: "100dvh",
          },
          body: { padding: 0, height: "100%", overflow: "hidden" },
        }}
        wrapClassName="global-search-modal-wrap"
        afterOpenChange={(visible) => {
          if (visible) inputRef.current?.focus();
        }}
      >
        <div
          className="global-search"
          onKeyDown={handleKeyDown}
          role="dialog"
          aria-label="Global search"
          aria-modal="true"
        >
          <header className="global-search__header">
            <div className="global-search__shell global-search__top-bar">
              <div className="global-search__query-wrap">
                <SearchOutlined className="global-search__query-icon" />
                <input
                  ref={inputRef}
                  className="global-search__query"
                  type="search"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="Search name, ID, or clinical term…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.metaKey && !e.ctrlKey) {
                      e.preventDefault();
                      e.stopPropagation();
                      runSearch();
                    }
                  }}
                  aria-label="Search"
                />
                {query ? (
                  <button
                    type="button"
                    className="global-search__query-clear"
                    onClick={() => setQuery("")}
                    aria-label="Clear query"
                  >
                    <CloseOutlined />
                  </button>
                ) : null}
              </div>
              <button
                type="button"
                className="global-search__close-btn"
                onClick={onClose}
                aria-label="Close search (Esc)"
              >
                <CloseOutlined />
              </button>
            </div>

            <div className="global-search__shell global-search__filters-row">
              <div className="global-search__filters">
                <div className="global-search__filters-bar">
                  <span className="global-search__filters-label">Resource</span>
                  <div
                    ref={categoriesRef}
                    className="global-search__filters-scroll"
                    role="tablist"
                    aria-label="Search resource"
                    onWheel={handleCategoriesWheel}
                  >
                {targetOptions.map((opt) => {
                  const Icon = TARGET_ICONS[opt.value] || SearchOutlined;
                  const active = opt.value === target;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      disabled={opt.disabled}
                      title={
                        opt.title ||
                        opt.catalog?.label ||
                        opt.label
                      }
                      className={`global-search__chip${active ? " global-search__chip--active" : ""}`}
                      onClick={() => {
                        if (!opt.disabled) setTarget(opt.value);
                      }}
                    >
                      <Icon className="global-search__chip-icon" />
                      <span className="global-search__chip-text">
                        {getChipLabel(opt)}
                      </span>
                    </button>
                  );
                })}
                  </div>
                </div>
              </div>
            </div>
          </header>

          {(catalogLoading ||
            catalogError ||
            feedback ||
            (showingCached && searched && !loading)) && (
            <div
              className={`global-search__banner${feedback ? " global-search__banner--warn" : ""}`}
            >
              <div className="global-search__shell">
                {catalogLoading && !catalogError ? "Syncing search catalog…" : null}
                {catalogError && !catalogLoading
                  ? `Catalog offline (${catalogError}) — using defaults`
                  : null}
                {showingCached && searched && !loading && !feedback ? (
                  <>
                    Showing your last search — press <strong>Enter</strong> to refresh
                  </>
                ) : null}
                {feedback || null}
              </div>
            </div>
          )}

          <main className="global-search__main">
            <div className="global-search__shell global-search__main-inner">
              {loading ? (
                <div className="global-search__state">
                  <Spin size="large" />
                  <p className="global-search__state-hint" style={{ marginTop: 16 }}>
                    Searching…
                  </p>
                </div>
              ) : !searched ? (
                <div className="global-search__state">
                  <div className="global-search__state-icon">
                    <SearchOutlined />
                  </div>
                  <p className="global-search__state-title">Ready when you are</p>
                  <p className="global-search__state-hint">{idleHint}</p>
                </div>
              ) : results.length === 0 ? (
                <div className="global-search__state">
                  <div className="global-search__state-icon">
                    <SearchOutlined />
                  </div>
                  <p className="global-search__state-title">No matches</p>
                  <p className="global-search__state-hint">
                    Try another term or switch category
                  </p>
                </div>
              ) : (
                <>
                  {sectionTitle ? (
                    <h3 className="global-search__section-title">{sectionTitle}</h3>
                  ) : null}
                  <GlobalSearchResultList
                    listTarget={target}
                    results={results}
                    activeIndex={activeIndex}
                    onSelect={rowSetting}
                    onHover={setActiveIndex}
                  />
                </>
              )}
            </div>
          </main>

          <footer className="global-search__footer">
            <div className="global-search__shell global-search__footer-inner">
              <div className="global-search__hints">
                <span className="global-search__hint">
                  <kbd>↑</kbd>
                  <kbd>↓</kbd>
                  Navigate
                </span>
                <span className="global-search__hint">
                  <kbd>Enter</kbd>
                  Select
                </span>
                <span className="global-search__hint">
                  <kbd>Esc</kbd>
                  Close
                </span>
                <span className="global-search__hint">
                  <kbd>⌘</kbd>
                  <kbd>Enter</kbd>
                  Full page
                </span>
              </div>
              <div className="global-search__footer-actions">
                {canViewFacilityExports ? (
                  <button
                    type="button"
                    className="global-search__advanced-link"
                    onClick={() => setFacilityExportsDrawerOpen(true)}
                  >
                    <ExportOutlined aria-hidden style={{ marginRight: 4 }} />
                    My exports
                  </button>
                ) : null}
                {showFacilityExport ? (
                  <button
                    type="button"
                    className="global-search__advanced-link"
                    onClick={() => setFacilityExportOpen(true)}
                  >
                    <DownloadOutlined aria-hidden style={{ marginRight: 4 }} />
                    Export
                  </button>
                ) : null}
                <button
                  type="button"
                  className="global-search__advanced-link"
                  onClick={goFullSearch}
                  disabled={!query.trim() && !allowEmptyForTarget}
                >
                  Advanced search
                </button>
                <button
                  type="button"
                  className="global-search__footer-cta"
                  onClick={goFullSearch}
                  disabled={!query.trim() && !allowEmptyForTarget}
                >
                  Full search page
                  <ExportOutlined />
                </button>
              </div>
            </div>
          </footer>
        </div>
      </Modal>

      {showClient && (
        <PolicyHolderModal
          display={showClient}
          data={rowData}
          action={refreshAfterDetail}
          showAllActions
          onclose={() => setShowClient(false)}
          viewOnly={!canEdit()}
          modalZIndex={GLOBAL_SEARCH_DETAIL_MODAL_Z_INDEX}
        />
      )}

      {showClientEnrollee ? (
        <ListOfDependent
          dependentList={[]}
          policy={rowData}
          showModal={showClientEnrollee}
          closeModal={handleCancelClientEnrollees}
          viewOnly={!canEdit()}
          modalZIndex={GLOBAL_SEARCH_DETAIL_MODAL_Z_INDEX}
        />
      ) : null}

      {showEnrollee && (
        <EnroleeBiodataModal
          data={rowData}
          display={showEnrollee}
          action={handleCancelEnrollees}
          command={refreshAfterDetail}
          source={1}
          viewOnly={!canEdit()}
          modalZIndex={GLOBAL_SEARCH_DETAIL_MODAL_Z_INDEX}
          openEncounterCodes={openEncounterCodes}
          onEncounterCodesClose={() => setOpenEncounterCodes(false)}
        />
      )}

      {showFacility && (
        <FacilityData
          data={rowData}
          display={showFacility}
          action={handleCancelFacilities}
          command={refreshAfterDetail}
          source={1}
          viewOnly={!canEdit()}
          modalZIndex={GLOBAL_SEARCH_DETAIL_MODAL_Z_INDEX}
        />
      )}

      <FacilitiesExportModal
        open={facilityExportOpen}
        onClose={() => setFacilityExportOpen(false)}
        search={query}
        estimatedRows={facilityExportRowEstimate}
        modalZIndex={GLOBAL_SEARCH_EXPORT_Z_INDEX}
        onViewExports={() => {
          setFacilityExportOpen(false);
          setFacilityExportsDrawerOpen(true);
        }}
      />

      <FacilitiesExportsDrawer
        open={facilityExportsDrawerOpen}
        onClose={() => setFacilityExportsDrawerOpen(false)}
        drawerZIndex={GLOBAL_SEARCH_EXPORT_Z_INDEX}
      />
    </>
  );
};

export default GlobalSearchOverlay;
